import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { ActivityListItem } from './types';
import { ActivityListItemNote } from './activity-list-item-note';
import { ListSection, RecyclerList } from '@/components/recycler-list';
import {
  RefreshControl,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface Props {
  activities: ListSection<ActivityListItem>[];
  fetchingMore: boolean;
  hasResponse: boolean;
  loading: boolean;
  refresh: () => void;
  refreshing: boolean;
  style: StyleProp<ViewStyle>;
}

export const ActivityList: React.FC<Props> = ({
  style,
  fetchingMore,
  activities,
  hasResponse,
  refresh,
  refreshing,
  loading,
}) => {
  const hasActivities = useMemo(() => {
    return activities.some((section) => section?.data.length > 0);
  }, [activities]);

  const showEmptyList = useMemo(() => {
    return !hasActivities && !refreshing && !loading;
  }, [hasActivities, refreshing, loading]);

  const showLoadingIndicator = useMemo(() => {
    return loading && !fetchingMore && !hasResponse && !refreshing;
  }, [hasResponse, refreshing, loading, fetchingMore]);

  return (
    <View style={[styles.activitiesList, style]}>
      <RecyclerList
        estimatedItemSize={50}
        ItemSeparatorComponent={null}
        ListFooterComponent={
          <View collapsable={false}>
            {fetchingMore ? (
              <ActivityIndicator testID="activity-list-footer" />
            ) : showEmptyList ? (
              <Text style={TYPOGRAPHY.small} testID="activity-list-empty">
                {i18n.t('activity_list.empty')}
              </Text>
            ) : null}
          </View>
        }
        ListHeaderComponent={
          showLoadingIndicator ? (
            <ActivityIndicator testID="activity-list-header-loading" />
          ) : (
            <Text
              style={[TYPOGRAPHY.h3, styles.activitiesTitle]}
              testID="activity-list-header"
            >
              {i18n.t('activity')}
            </Text>
          )
        }
        refreshControl={
          <RefreshControl
            onRefresh={refresh}
            refreshing={refreshing && !(loading && hasResponse)}
            testID="activity-list-refresh-control"
          />
        }
        renderItem={({ item }: { item: ActivityListItem }) => {
          return (
            <ActivityListItemNote
              content={item.title}
              createdAt={item.metadata.createdAt}
              employeeName={item.metadata.employeeName}
              key={item.value}
              reminderAt={item.metadata.reminderAt}
            />
          );
        }}
        sections={activities}
        showsVerticalScrollIndicator={false}
        testID="activity-list"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  activitiesList: {
    marginTop: 8,
  },
  activitiesTitle: {
    marginVertical: 8,
  },
});
